/**
 * Observations Service
 *
 * Service for interacting with the Observations NGXS state.
 * Provides methods for loading, filtering, and managing observations.
 */

import { Injectable } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import {
  InitializeObservations,
  InitializeObservationsSuccess,
  InitializeObservationsFailure,
  LoadNextBatch,
  LoadNextBatchSuccess,
  LoadNextBatchFailure,
  SetObservationsFilters,
  SetObservationsSort,
  AddObservation,
  AddObservationSuccess,
  AddObservationFailure,
  UpdateObservationsFromListener,
  ClearObservations,
  ClearObservationsError,
} from './observations.actions';
import { ObservationsState, ObservationsStateModel } from './observations.state';
import { Observation } from 'src/app/models/schema';
import { FirestoreService } from 'src/app/services/firestore/firestore';

@Injectable({
  providedIn: 'root',
})
export class ObservationsService {
  private readonly PAGE_SIZE = 25;

  /**
   * Current partner ID for determining subcollection path
   */
  private currentPartnerId: string | null = null;

  /**
   * Last document snapshot for pagination
   */
  private lastDocument: any = null;

  /**
   * Observable of the entire observations state
   */
  @Select(ObservationsState.getState) state$!: Observable<ObservationsStateModel>;

  /**
   * Observable of all observations
   */
  @Select(ObservationsState.observations) observations$!: Observable<Observation[]>;

  /**
   * Observable of observations sorted by date
   */
  @Select(ObservationsState.observationsSorted) observationsSorted$!: Observable<Observation[]>;

  /**
   * Observable of loading state
   */
  @Select(ObservationsState.loading) loading$!: Observable<boolean>;

  /**
   * Observable of initialized state
   */
  @Select(ObservationsState.initialized) initialized$!: Observable<boolean>;

  /**
   * Observable of error state
   */
  @Select(ObservationsState.error) error$!: Observable<string | null>;

  /**
   * Observable of hasMore pagination state
   */
  @Select(ObservationsState.hasMore) hasMore$!: Observable<boolean>;

  /**
   * Observable of current filters
   */
  @Select(ObservationsState.filters) filters$!: Observable<any>;

  /**
   * Observable of current sort
   */
  @Select(ObservationsState.sort) sort$!: Observable<{ field: string; direction: 'asc' | 'desc' }>;

  constructor(
    private store: Store,
    private firestoreService: FirestoreService
  ) {}

  /**
   * Get current observations snapshot
   */
  get observations(): Observation[] {
    return this.store.selectSnapshot(ObservationsState.observations);
  }

  /**
   * Get current loading state snapshot
   */
  get loading(): boolean {
    return this.store.selectSnapshot(ObservationsState.loading);
  }

  /**
   * Get current initialized state snapshot
   */
  get initialized(): boolean {
    return this.store.selectSnapshot(ObservationsState.initialized);
  }

  /**
   * Get current hasMore state snapshot
   */
  get hasMore(): boolean {
    return this.store.selectSnapshot(ObservationsState.hasMore);
  }

  /**
   * Initialize observations - load first batch
   * @param partnerId - The partner ID to load observations for
   */
  async initialize(partnerId: string): Promise<void> {
    try {
      this.currentPartnerId = partnerId;
      this.lastDocument = null; // Reset pagination cursor

      await this.store.dispatch(new InitializeObservations(partnerId)).toPromise();

      const state = this.store.selectSnapshot(ObservationsState.getState);
      const { filters, sortField, sortDirection } = state;

      // Build query path
      const collectionPath = `partners/${partnerId}/observations`;

      // Build query options — no orderBy to avoid requiring a composite Firestore index.
      // Sorting is applied locally after fetch.
      const queryOptions: any = {
        limit: this.PAGE_SIZE + 1, // Request one extra to check if more exist
      };

      // Add filters if present
      if (filters && Object.keys(filters).length > 0) {
        queryOptions.where = [];
        
        // Handle date range filters
        if (filters.startDate) {
          queryOptions.where.push({ field: 'createdAt', operator: '>=', value: filters.startDate });
        }
        if (filters.endDate) {
          queryOptions.where.push({ field: 'createdAt', operator: '<=', value: filters.endDate });
        }

        // Handle other filters (exact match)
        for (const [field, value] of Object.entries(filters)) {
          if (field !== 'startDate' && field !== 'endDate' && value !== null && value !== undefined && value !== '') {
            queryOptions.where.push({ field, operator: '==', value });
          }
        }
      }

      // Fetch first batch
      const results = await this.firestoreService.getCollectionAsync<Observation>(
        collectionPath,
        queryOptions
      );

      const hasMore = results.length > this.PAGE_SIZE;
      // Sort locally instead of relying on Firestore orderBy
      const observations = (hasMore ? results.slice(0, this.PAGE_SIZE) : results)
        .sort((a: any, b: any) => {
          const aVal = a[sortField] ?? 0;
          const bVal = b[sortField] ?? 0;
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        });

      // Store last document for pagination
      if (observations.length > 0) {
        const lastObs = observations[observations.length - 1] as any;
        this.lastDocument = lastObs[sortField];
      }

      await this.store.dispatch(
        new InitializeObservationsSuccess(observations, hasMore)
      ).toPromise();
    } catch (error: any) {
      console.error('[ObservationsService] Error initializing:', error);
      await this.store.dispatch(
        new InitializeObservationsFailure(error.message || 'Failed to load observations')
      ).toPromise();
      throw error;
    }
  }

  /**
   * Load next batch of observations
   */
  async loadNextBatch(): Promise<void> {
    const state = this.store.selectSnapshot(ObservationsState.getState);
    
    if (!state.hasMore || state.loading || !this.lastDocument) {
      return;
    }

    try {
      await this.store.dispatch(new LoadNextBatch()).toPromise();

      const { partnerId, filters, sortField, sortDirection } = state;

      if (!partnerId) {
        throw new Error('No partner ID set');
      }

      // Build query path
      const collectionPath = `partners/${partnerId}/observations`;

      // No orderBy (avoids composite index requirement) and no startAfter cursor
      // (cursor-based pagination requires orderBy). Sort locally after fetch.
      const queryOptions: any = {
        limit: this.PAGE_SIZE + 1,
      };

      // Add filters if present
      if (filters && Object.keys(filters).length > 0) {
        queryOptions.where = [];
        
        // Handle date range filters
        if (filters.startDate) {
          queryOptions.where.push({ field: 'createdAt', operator: '>=', value: filters.startDate });
        }
        if (filters.endDate) {
          queryOptions.where.push({ field: 'createdAt', operator: '<=', value: filters.endDate });
        }

        // Handle other filters (exact match)
        for (const [field, value] of Object.entries(filters)) {
          if (field !== 'startDate' && field !== 'endDate' && value !== null && value !== undefined && value !== '') {
            queryOptions.where.push({ field, operator: '==', value });
          }
        }
      }

      // Fetch next batch
      const results = await this.firestoreService.getCollectionAsync<Observation>(
        collectionPath,
        queryOptions
      );

      const hasMore = results.length > this.PAGE_SIZE;
      // Sort locally
      const newObservations = (hasMore ? results.slice(0, this.PAGE_SIZE) : results)
        .sort((a: any, b: any) => {
          const aVal = a[sortField] ?? 0;
          const bVal = b[sortField] ?? 0;
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        });

      // Update last document for next pagination
      if (newObservations.length > 0) {
        const lastObs = newObservations[newObservations.length - 1] as any;
        this.lastDocument = lastObs[sortField];
      }

      await this.store.dispatch(
        new LoadNextBatchSuccess(newObservations, hasMore)
      ).toPromise();
    } catch (error: any) {
      console.error('[ObservationsService] Error loading next batch:', error);
      await this.store.dispatch(
        new LoadNextBatchFailure(error.message || 'Failed to load more observations')
      ).toPromise();
      throw error;
    }
  }

  /**
   * Set filter criteria and reload
   * @param filters - Filter object with field names and values
   */
  async setFilters(filters: any): Promise<void> {
    await this.store.dispatch(new SetObservationsFilters(filters)).toPromise();
    
    // Reload with new filters
    const partnerId = this.store.selectSnapshot(ObservationsState.partnerId);
    if (partnerId) {
      await this.initialize(partnerId);
    }
  }

  /**
   * Set sort criteria and reload
   * @param sortField - Field to sort by
   * @param sortDirection - Sort direction
   */
  async setSort(sortField: string, sortDirection: 'asc' | 'desc'): Promise<void> {
    await this.store.dispatch(new SetObservationsSort(sortField, sortDirection)).toPromise();
    
    // Reload with new sort
    const partnerId = this.store.selectSnapshot(ObservationsState.partnerId);
    if (partnerId) {
      await this.initialize(partnerId);
    }
  }

  /**
   * Add a new observation
   * @param observation - The observation to add
   */
  async addObservation(observation: Observation): Promise<void> {
    try {
      await this.store.dispatch(new AddObservation(observation)).toPromise();
      await this.store.dispatch(new AddObservationSuccess(observation)).toPromise();
    } catch (error: any) {
      console.error('[ObservationsService] Error adding observation:', error);
      await this.store.dispatch(
        new AddObservationFailure(error.message || 'Failed to add observation')
      ).toPromise();
      throw error;
    }
  }

  /**
   * Clear all observations data
   */
  async clear(): Promise<void> {
    await this.store.dispatch(new ClearObservations()).toPromise();
  }

  /**
   * Clear error state
   */
  async clearError(): Promise<void> {
    await this.store.dispatch(new ClearObservationsError()).toPromise();
  }
}
